import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { UNASSIGNED, UNKNOWN } from "core/constants";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetUsers } from "hooks/users/useLazyGetUsers";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface OptionType {
  id: number | string;
  name: string;
  email: string | null;
}

interface UserFilterProps {
  title?: string;
  selectedUsers: OptionType[];
  onChangeSelectedUsers: (values: OptionType[]) => void;
  showChips?: boolean;
  emptyOption?: "unassigned" | "unknown";
}

interface FormattedUsers {
  id: number;
  name: string;
  email: string;
}

const selectAllOption: OptionType = {
  id: -1,
  name: "Select All",
  email: ""
};

export const UserFilter = ({
  title = "User",
  selectedUsers,
  onChangeSelectedUsers,
  showChips = true,
  emptyOption = undefined
}: UserFilterProps): JSX.Element => {
  const {
    loading: userLoading,
    error: userError,
    fetchUsers: getUsers
  } = useLazyGetUsers({});
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getUsers().then(response => {
        if (response.results) {
          let unassignedUser = {
            name: emptyOption === "unassigned" ? UNASSIGNED : UNKNOWN,
            id: "Unassigned",
            email: null
          };

          const formattedUsers: FormattedUsers[] = response.results
            .filter(user => user.is_active)
            .map(user => ({
              id: user.id,
              name: `${user.first_name} ${user.last_name}`,
              email: `${user.email}`
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          setOptions([selectAllOption, unassignedUser, ...formattedUsers]);
        }
      });
    }
    setSelectedState(selectedUsers);
  }, [options, getUsers, emptyOption, selectedUsers]);

  return (
    <StyledAutocomplete
      label={title}
      placeholder={showChips ? title : ""}
      loading={userLoading}
      error={userError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={() => null}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.id === value.id
      }
      value={selectedUsers}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;
        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");
        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        const emailString = option.email ? `(${option.email})` : "";

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name} {emailString}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );
            onChangeSelectedUsers(selected);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedUsers, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedUsers(selected);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedUsers(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
